import React, { useEffect, useRef, useState } from 'react';
import ChatPanel from './ChatPanel';
import VerificationWidget from './VerificationWidget';
import { fetchAnonymousSession } from '../services/session';
import type { ChatMessage, MessageRatingValue } from '../types';
import { getSessionStorage } from '../../../../utils/browserStorage';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  messages: ChatMessage[];
  isLoading: boolean;
  isSessionLimitReached?: boolean;
  forceVerified?: boolean;
  onSend: (text: string) => void;
  onReset: () => void;
  seenMessageIds: Set<string>;
  onQueryClick?: (url: string) => void;
  onFacetClick?: (label: string) => void;
  onSuggestionClick?: (text: string) => void;
  verificationBaseUrl?: string;
  turnstileSiteKey?: string;
  allowLocalDevBypass?: boolean;
  showDebugInfo?: boolean;
  showSessionId?: boolean;
  placeholder?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  greetingMessage?: string;
  projectInfoUrl?: string;
  projectInfoLinkText?: string;
  forceReVerify?: boolean;
  onReVerifyHandled?: () => void;
  onVerified?: () => void;
  feedbackEnabled?: boolean;
  feedbackStyle?: 'stars' | 'thumbs';
  feedbackTagsPositive?: string[];
  feedbackTagsNegative?: string[];
  onSubmitFeedback?: (
    messageId: string,
    value: MessageRatingValue,
    comment: string,
    tags: string[],
  ) => Promise<void>;
}

/**
 * A popup modal/dialog that wraps the ChatPanel.
 * Expands from the FAB button position (bottom-right) with a smooth
 * scale + fade animation. Includes a toggle to switch between
 * compact (widget) and expanded (large) view for charts/data.
 */
const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  messages,
  isLoading,
  isSessionLimitReached,
  forceVerified,
  onSend,
  onReset,
  seenMessageIds,
  onQueryClick,
  onFacetClick,
  onSuggestionClick,
  verificationBaseUrl,
  turnstileSiteKey,
  allowLocalDevBypass,
  showDebugInfo,
  showSessionId,
  placeholder,
  headerTitle,
  headerSubtitle,
  greetingMessage,
  projectInfoUrl,
  projectInfoLinkText,
  forceReVerify,
  onReVerifyHandled,
  onVerified,
  feedbackEnabled,
  feedbackStyle,
  feedbackTagsPositive,
  feedbackTagsNegative,
  onSubmitFeedback,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen && typeof document !== 'undefined') {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Focus modal when open
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (forceVerified) {
      setIsVerified(true);
      return;
    }
    const captchaDisabled = !turnstileSiteKey || !turnstileSiteKey.trim();
    const existingToken = getSessionStorage()?.getItem('sessionToken');
    const hasUsableToken = Boolean(
      existingToken && existingToken !== 'local-dev-bypass',
    );
    if (!captchaDisabled) {
      // Normal CAPTCHA flow — check for existing token
      setIsVerified(hasUsableToken);
      return;
    }
    // CAPTCHA disabled — check for existing token first
    if (hasUsableToken) {
      setIsVerified(true);
      return;
    }
    // No valid token — try to silently get one from the verify endpoint
    if (verificationBaseUrl) {
      fetchAnonymousSession(verificationBaseUrl).then((ok) => {
        setIsVerified(true); // proceed either way; API will 401 if backend won't allow it
        if (!ok) {
          // eslint-disable-next-line no-console
          console.warn(
            '[VES] Anonymous session fetch failed. Backend may require CAPTCHA verification.',
          );
        }
      });
    } else {
      // No verification endpoint configured — proceed without a token
      setIsVerified(true);
    }
  }, [forceVerified, isOpen, turnstileSiteKey, verificationBaseUrl]);

  // Handle forced re-verification (e.g. session expired / 401)
  useEffect(() => {
    if (!forceReVerify) return;
    const captchaDisabled = !turnstileSiteKey || !turnstileSiteKey.trim();
    getSessionStorage()?.removeItem('sessionToken');
    onReVerifyHandled?.();
    if (captchaDisabled && verificationBaseUrl) {
      // Try to silently refresh the anonymous session
      fetchAnonymousSession(verificationBaseUrl).then(() =>
        setIsVerified(true),
      );
    } else if (captchaDisabled) {
      setIsVerified(true);
    } else {
      setIsVerified(false); // show CAPTCHA widget
    }
  }, [forceReVerify, onReVerifyHandled, turnstileSiteKey, verificationBaseUrl]);

  // Reset to compact when closed
  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setIsVerified(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isVerified) {
      onVerified?.();
    }
  }, [isOpen, isVerified, onVerified]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="ves-modal-backdrop"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="presentation"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Enhanced Search Agent"
        className={`ves-modal ${isExpanded ? 'ves-modal--expanded' : ''}`}
      >
        {isVerified ? (
          <ChatPanel
            sessionId={sessionId}
            messages={messages}
            isLoading={isLoading}
            isSessionLimitReached={isSessionLimitReached}
            onSend={onSend}
            onReset={onReset}
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded((prev) => !prev)}
            seenMessageIds={seenMessageIds}
            onQueryClick={onQueryClick}
            onFacetClick={onFacetClick}
            onSuggestionClick={onSuggestionClick}
            placeholder={placeholder}
            headerTitle={headerTitle}
            headerSubtitle={headerSubtitle}
            greetingMessage={greetingMessage}
            projectInfoUrl={projectInfoUrl}
            projectInfoLinkText={projectInfoLinkText}
            showDebugInfo={showDebugInfo}
            showSessionId={showSessionId}
            feedbackEnabled={feedbackEnabled}
            feedbackStyle={feedbackStyle}
            feedbackTagsPositive={feedbackTagsPositive}
            feedbackTagsNegative={feedbackTagsNegative}
            onSubmitFeedback={onSubmitFeedback}
          />
        ) : (
          <VerificationWidget
            onValidated={() => setIsVerified(true)}
            onClose={onClose}
            verificationBaseUrl={verificationBaseUrl}
            turnstileSiteKey={turnstileSiteKey}
            allowLocalDevBypass={allowLocalDevBypass}
            headerTitle={headerTitle}
            headerSubtitle={headerSubtitle}
            showDebugInfo={showDebugInfo}
          />
        )}
      </div>
    </>
  );
};

export default ChatModal;
